import React from 'react'
import {
  Toolbar,
  Box,
  Button,
  IconButton,
  CircularProgress,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import {
  Check as CheckIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material'

interface SaveToolbarProps {
  onSave: () => void | Promise<void>
  onDelete?: () => void | Promise<void>
  saveDisabled?: boolean
  deleteDisabled?: boolean
  loading?: boolean
  deleteMode?: boolean // Changes button text to "Save & Delete" and color to red
  showDelete?: boolean
  saveText?: string
  className?: string
}

const SaveToolbar: React.FC<SaveToolbarProps> = ({
  onSave,
  onDelete,
  saveDisabled = false,
  deleteDisabled = false,
  loading = false,
  deleteMode = false,
  showDelete = true,
  saveText,
  className = '',
}) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const handleSave = async () => {
    if (!saveDisabled && !loading) {
      await onSave()
    }
  }

  const handleDelete = async () => {
    if (onDelete && !deleteDisabled && !loading) {
      await onDelete()
    }
  }

  const getSaveButtonText = () => {
    if (saveText) return saveText
    if (deleteMode) return 'Save & Delete'
    return 'Save'
  }

  const getSaveButtonColor = () => {
    return deleteMode ? 'error' : 'inherit'
  }

  return (
    <Toolbar
      className={`save-toolbar ${className}`}
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: theme.zIndex.appBar - 1,
        backgroundColor: 'background.paper',
        borderBottom: 1,
        borderColor: 'divider',
        minHeight: { xs: 56, sm: 64 },
        px: { xs: 2, sm: 3 },
      }}
    >
      <Box sx={{ flexGrow: 1 }} />

      {/* Delete Button */}
      {showDelete && onDelete && (
        <>
          {isMobile ? (
            <IconButton
              color="error"
              onClick={handleDelete}
              disabled={deleteDisabled || loading}
              sx={{ mr: 2 }}
            >
              <DeleteIcon />
            </IconButton>
          ) : (
            <IconButton
              color="error"
              onClick={handleDelete}
              disabled={deleteDisabled || loading}
              sx={{ mr: 4 }}
            >
              <DeleteIcon />
            </IconButton>
          )}
        </>
      )}

      {/* Save Button */}
      <Button
        variant="outlined"
        color={getSaveButtonColor()}
        onClick={handleSave}
        disabled={saveDisabled || loading}
        startIcon={
          loading ? (
            <CircularProgress
              size={18}
              thickness={2}
              color="inherit"
              sx={{ ml: -0.5, mr: 0.5 }}
            />
          ) : (
            <CheckIcon />
          )
        }
        sx={{
          borderRadius: 0, // Tile style
          borderColor: deleteMode ? 'error.main' : '#000',
          color: deleteMode ? 'error.main' : '#000',
          fontWeight: 600,
          textTransform: 'none',
          minWidth: isMobile ? 140 : 120,
          px: 3,
          '&:hover': {
            borderColor: deleteMode ? 'error.dark' : '#000',
            backgroundColor: deleteMode
              ? 'rgba(211, 47, 47, 0.04)'
              : 'rgba(0, 0, 0, 0.04)',
          },
          '&.Mui-disabled': {
            borderColor: 'action.disabled',
            color: 'action.disabled',
          },
        }}
        className={isMobile ? 'save-button-mobile-wide' : ''}
      >
        {loading ? 'Saving...' : getSaveButtonText()}
      </Button>
    </Toolbar>
  )
}

export default SaveToolbar
